import { useRouter } from 'next/router';
import { useCallback, useState, useRef, useMemo, useEffect } from 'react';
import { Menu, Transition } from '@headlessui/react';
import useSWR from 'swr';
import { Fragment, KeyboardEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faSearch,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import type { DealSkuCardType } from '~/components/sku/types';
import useAttributes from '~/lib/attributes/hook';

export default function SearchInput({
  isCollectionBrowsePage,
}: {
  isCollectionBrowsePage?: boolean;
}) {
  const [search, setSearch] = useState<string>('');
  const [finalSearch, setFinalSearch] = useState<string>('');
  const [isFocus, setIsFocus] = useState<boolean>(false);
  const [focusedItemIndex, setFocusedItemIndex] = useState<number>(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const isTrending = !search;
  const router = useRouter();
  const { data } = useSWR(() =>
    isFocus
      ? finalSearch
        ? `/api/v2/deals/esbrowse/?status=all&sort=recommended&query=${finalSearch}&per_page=5&autocomplete=1`
        : `/api/v2/deals/esbrowse/?status=current&sort=recommended&toptrending=1&per_page=5`
      : null,
  );
  const { data: attributes } = useAttributes();

  const groups = useMemo(() => {
    const group = attributes?.find((attr: any) => attr.slug === 'group');
    return group?.type_options.options.map((o: any) => o.value) || [];
  }, [attributes]);

  const deals: DealSkuCardType[] = data?.deals || [];
  const handleClearSearch = useCallback(() => {
    setSearch('');
    inputRef?.current?.focus();
  }, [inputRef]);

  const handleClickDeal = (deal: DealSkuCardType) => {
    window.location.href = `/products/${deal.slug}/?query=${deal.public_name}`;
  };

  const onBlurParentContainer = (event: React.FocusEvent<HTMLElement>) => {
    const { currentTarget } = event;

    requestAnimationFrame(() => {
      // if focus is not on child, unfocus
      if (!currentTarget.contains(document.activeElement)) {
        setIsFocus(false);
      }
    });
  };

  // Adds the url query to the search input on page "load" (there is a delay unfortunately)
  // We only need to do this once, so ignore changes to the query
  useEffect(() => {
    if (!router.isReady) return;
    setSearch((router.query.query as string) ?? '');
  }, [router.isReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the route changes check if we need to clear the search (non ES Browse pages)
  useEffect(() => {
    if (!isCollectionBrowsePage && search) {
      setSearch('');
    }
    // Only update if there is a search query, we don't want to update everytime search changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCollectionBrowsePage]);

  const handleSubmitSearch = () => {
    if (focusedItemIndex > -1) {
      handleClickDeal(deals[focusedItemIndex]);
    } else {
      // go to the search page (or the current group page)
      const parts = router.asPath.split('/').filter(Boolean);
      const group =
        parts.length > 0 && groups.includes(parts[0]) ? parts[0] : 'search';
      router.push(`/${group}/?query=${search}`);
    }
    inputRef?.current?.blur();
    searchButtonRef?.current?.blur();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    let newIndex = -1;
    if (event.code === 'ArrowDown' || event.key === 'ArrowDown') {
      event.preventDefault();
      // if last item, go to first item
      if (focusedItemIndex === null || focusedItemIndex === deals.length - 1) {
        newIndex = 0;
      } else {
        newIndex = focusedItemIndex + 1;
      }
    } else if (event.code === 'ArrowUp' || event.key === 'ArrowUp') {
      event.preventDefault();

      // if first item, go to last item
      if (focusedItemIndex === null || focusedItemIndex === 0) {
        newIndex = deals.length - 1;
      } else {
        newIndex = focusedItemIndex - 1;
      }
    } else if (event.code === 'Enter' || event.key === 'Enter') {
      event.preventDefault();
      handleSubmitSearch();
    }
    setFocusedItemIndex(newIndex);

    if (newIndex !== -1) {
      setSearch(deals[newIndex].public_name);
    }
  };

  const handleKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
    // If key is not arrow up, down, or enter, update the search
    if (
      event.code !== 'ArrowDown' &&
      event.code !== 'ArrowUp' &&
      event.code !== 'Enter' &&
      event.key !== 'ArrowDown' &&
      event.key !== 'ArrowUp' &&
      event.key !== 'Enter'
    ) {
      setFinalSearch(search);
    }
  };
  return (
    <div className="order-4 col-[full] max-md:pb-[16px] md:order-none md:col-auto md:px-[32px] xl:pl-[97px]">
      <Menu>
        {() => (
          <div
            className="relative flex rounded-[30px] border border-grace bg-iceberg px-1 py-[1px] shadow-lg shadow-black/10 focus-within:border-2 focus-within:border-bolt focus-within:px-[3px] focus-within:py-0.5"
            onFocus={() => setIsFocus(true)}
            onBlur={onBlurParentContainer}
          >
            <input
              type="text"
              className="mx-3 my-1 w-full border-none bg-iceberg text-black-pearl outline-none"
              placeholder="Search products"
              autoComplete="off"
              value={search}
              ref={inputRef}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              onKeyUp={handleKeyUp}
            />
            <div className="flex items-center gap-x-1">
              {!isTrending && (
                <>
                  <button
                    className="flex items-center justify-center px-2 py-1"
                    onClick={handleClearSearch}
                  >
                    <span className="sr-only">Clear search</span>
                    <FontAwesomeIcon
                      icon={faTimes}
                      className="text-xl font-bold text-grace"
                    />
                  </button>
                  <div className="border-r border-grace">&nbsp;</div>
                </>
              )}
              <button
                className="flex items-center justify-center rounded-[30px] px-4 py-1 transition-colors duration-300 ease-in-out hover:bg-lemon"
                onClick={handleSubmitSearch}
                ref={searchButtonRef}
              >
                <span className="sr-only">Submit search</span>
                <FontAwesomeIcon
                  icon={faSearch}
                  className="text-xl font-bold text-grace"
                />
              </button>
            </div>
            <Transition
              as={Fragment}
              show={isFocus && deals?.length > 0}
              enter="transition duration-300 ease-out"
              enterFrom="transform opacity-0"
              enterTo="transform opacity-100"
              leave="transition duration-300 ease-out"
              leaveFrom="transform opacity-100"
              leaveTo="transform opacity-0"
            >
              <Menu.Items className="absolute mt-10 flex w-full flex-col border border-gray-300 bg-white text-black-pearl outline-none">
                <div className="px-4 py-2 text-sm font-bold">
                  <FontAwesomeIcon
                    icon={isTrending ? faChartLine : faSearch}
                    className="mr-2"
                  />
                  {isTrending ? 'Trending Searches' : 'Suggested Searches'}
                </div>
                {deals.map((deal, index) => (
                  <Menu.Item key={deal.id}>
                    <button
                      onClick={() => handleClickDeal(deal)}
                      className={`cursor-pointer px-4 py-2 text-left text-sm hover:bg-iceberg ${
                        index === focusedItemIndex && 'bg-iceberg'
                      }`}
                    >
                      <strong>{deal.public_name}</strong>
                      {deal?.attributes?.category &&
                        deal?.attributes?.category.length > 0 && (
                          <span className="text-xs">
                            &nbsp;in {deal?.attributes.category[0]}
                          </span>
                        )}
                    </button>
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Transition>
          </div>
        )}
      </Menu>
    </div>
  );
}
